import React, { useEffect, useRef, useCallback, useState } from 'react';
import { createChart, CrosshairMode, LineStyle, CandlestickSeries, LineSeries } from 'lightweight-charts';
import { useTranslation } from '../i18n/LanguageContext';
import { useTheme } from '../hooks/useTheme';
import useChartDrawings from '../hooks/useChartDrawings';

const getIntervals = (t) => [
  { label: t('backtest.interval1m'),  value: '1m'  },
  { label: t('backtest.interval5m'),  value: '5m'  },
  { label: t('backtest.interval15m'), value: '15m' },
  { label: t('backtest.interval30m'), value: '30m' },
  { label: '1h',   value: '1H'  },
  { label: '4h',   value: '4H'  },
  { label: '1D',   value: '1D'  },
];

// lightweight-charts CSS değişkenlerini anlamaz — bu yüzden bg/text/grid/border tema'ya göre
// burada elle iki set olarak tutulur. Gösterge renkleri (up/down/ma50/...) marka/anlam tutarlılığı
// için HER İKİ temada da SABİT kalır (bkz. plan notu).
function getChartColors(theme) {
  const isDark = theme === 'dark';
  return {
    bg:     isDark ? '#1a2028' : '#ffffff',
    text:   isDark ? '#e2e8f0' : '#1e293b',
    grid:   isDark ? '#232a33' : '#f1f5f9',
    border: isDark ? '#313a46' : '#e2e8f0',
    up:     '#0d9488',
    down:   '#dc2626',
    ma50:   '#ef4444',
    ma200:  '#3b82f6',
    hma:    '#7c3aed',
    rsi:    isDark ? '#93c5fd' : '#172554',
    rsiMa:  '#0d9488',
    adx:    '#f59e0b',
  };
}

const PRICE_PANE = 0;
const IND_PANE   = 1;

function fmtPrice(price) {
  if (price == null) return '—';
  const a = Math.abs(price);
  if (a >= 10000) return price.toFixed(1);
  if (a >= 1000)  return price.toFixed(2);
  if (a >= 10)    return price.toFixed(3);
  if (a >= 1)     return price.toFixed(4);
  if (a >= 0.01)  return price.toFixed(5);
  return price.toFixed(6);
}

function fmtNum(v, d = 1) { return v == null ? '—' : v.toFixed(d); }

// İşaretli (+/-) fiyat/yüzde biçimlendirme — mum yeşilse (+), kırmızıysa (-).
function fmtPriceSigned(v) { return v == null ? '—' : `${v >= 0 ? '+' : '-'}${fmtPrice(Math.abs(v))}`; }
function fmtPctSigned(v, d = 2) { return v == null ? '—' : `${v >= 0 ? '+' : ''}${v.toFixed(d)}%`; }

// Mum istatistikleri: A/K = Açılış/Kapanış farkı (mum gövdesi), Maks/Min = High-Low aralığı
// (doğal olarak işaretsiz olduğu için mum rengine göre +/- işareti ZORLA uygulanır).
function candleStats(o, h, l, c) {
  if (o == null || h == null || l == null || c == null) return null;
  const green   = c >= o;
  const sign    = green ? 1 : -1;
  const akVal   = c - o;
  const akPct   = o !== 0 ? (akVal / o) * 100 : null;
  const rangeVal = Math.abs(h - l);
  const rangePct = l !== 0 ? (rangeVal / l) * 100 : null;
  return {
    green,
    akVal, akPct,
    mmVal: sign * rangeVal, mmPct: rangePct != null ? sign * rangePct : null,
  };
}

export default function Chart({ symbol, token, analysisTs, currentPrice, openTrade, settingsSavedAt, liveAnalysis, onAdjustTpSl, market = 'futures' }) {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const INTERVALS = getIntervals(t);
  const themeRef = useRef(theme);
  themeRef.current = theme;
  const containerRef = useRef(null);
  const charts       = useRef(null);
  const initialised  = useRef(false);
  const openTradeRef = useRef(openTrade);
  openTradeRef.current = openTrade;

  // Trend Çizgisi / Fiyat Aralığı / Yatay-Dikey Çizgi — bkz. useChartDrawings.js. Sunucuya
  // kaydedilmez, sadece bu tarayıcıda (sembole özel) saklanır.
  const { activeTool, setActiveTool, clearAll, attachTo, resizeCanvas, canvasRef: drawCanvasRef } = useChartDrawings(symbol);

  // TP/SL sürükle-bırak — SADECE manuel (signal_type==='manual') açık işlemlerde aktif. Bot
  // işlemlerinde bu tutamaçlar HİÇ render edilmez, bot'un TP/SL'i kullanıcı tarafından asla
  // değiştirilemez (kasıtlı güvenlik sınırı).
  const [handlePos, setHandlePos] = useState({ tp: null, sl: null }); // {price,y} — çizginin ekrandaki konumu
  const [ghost,     setGhost]     = useState({ tp: null, sl: null }); // sürükleme sırasında canlı önizleme
  const dragRef  = useRef({ side: null, startClientY: 0, startY: 0 });
  const ghostRef = useRef(ghost); ghostRef.current = ghost;
  const recalcHandlePositionsRef = useRef(() => {});
  const isManualTrade = openTrade?.signal_type === 'manual' && openTrade?.status !== 'closed';

  const [candleInterval, setCandleInterval] = useState('');
  const [strategy,       setStrategy]       = useState('ma50_rsi');
  const strategyRef = useRef(strategy);
  strategyRef.current = strategy;
  const [showHma, setShowHma] = useState(false);
  const [showAdx, setShowAdx] = useState(false);
  const showHmaRef = useRef(showHma); showHmaRef.current = showHma;
  const showAdxRef = useRef(showAdx); showAdxRef.current = showAdx;
  // MA50+RSI
  const [ma50Val,  setMa50Val]  = useState(null);
  const [ma200Val, setMa200Val] = useState(null);
  const [rsiVal,   setRsiVal]   = useState(null);
  const [rsiMaVal, setRsiMaVal] = useState(null);
  // HMA+ADX
  const [hmaVal,   setHmaVal]   = useState(null);
  const [adxVal,   setAdxVal]   = useState(null);
  const [hmaDir,   setHmaDir]   = useState(null); // 1 = yukarı, -1 = aşağı, null = bilinmiyor — CANLI (son) değer, hover yokken kullanılır
  // Ham HMA serisi (checkbox kapalı olsa bile) — crosshair'in hovered noktasındaki HMA yönünü
  // (bir önceki noktaya göre) zaman bazlı arayarak hesaplayabilmek için saklanır.
  const hmaDataRef = useRef([]);
  // En son (kapanmış) mumun OHLC'si — crosshair grafikten çıkınca buna düşülür.
  const [lastCandle, setLastCandle] = useState(null);
  // Piyasa geneli Long/Short oranı (OKX public gösterge) — candles ile AYNI 10sn'lik canlı
  // döngüde (fetchAndDraw) güncellenir, ayrı bir polling YOKTUR (bkz. backend chart.js route notu).
  const [longShortRatio, setLongShortRatio] = useState(null);
  // Fiyat pane'inin GERÇEK yüksekliği — başlangıçta 320 (bkz. panes[0].setHeight(320) aşağıda),
  // ama kullanıcı pane ayırıcısını sürükleyip küçültebilir. Çizim araç çubuğu + Long/Short bar'ı
  // (düz HTML flex sütunu) bu canvas pane'lerinden TAMAMEN bağımsız render edilir — hizalarını
  // otomatik takip etmezler. Bu yüzden burada periyodik olarak (bkz. aşağıdaki useEffect) gerçek
  // pane yüksekliği okunup toolbar'a maxHeight olarak uygulanır; aksi halde kullanıcı fiyat panelini
  // küçültürse toolbar/bar aynı yükseklikte kalıp RSI panelinin hizasına taşabilir.
  const [pricePaneHeight, setPricePaneHeight] = useState(320);
  // Fare grafik üzerinde gezerken o anki mumun OHLC + gösterge değerleri (OKX/TradingView'daki
  // gibi) — crosshair grafikten ayrılınca null'a döner, legend "canlı/son" değerlere geri düşer.
  const [hover, setHover] = useState(null);

  const handleReset = useCallback(() => {
    if (!charts.current) return;
    charts.current.chart.timeScale().fitContent();
    charts.current.candleSeries.priceScale().applyOptions({ autoScale: true });
  }, []);

  // Bir periyot geçişi, önceki periyodun isteği hâlâ havadayken yeni bir fetchAndDraw tetikleyebilir
  // (kullanıcı hızlıca değiştirirse ya da ağ yavaşsa). Yanıtlar isteklerle AYNI sırada gelmeyi garanti
  // etmez — eski (geç gelen) bir yanıt, zaten uygulanmış yeni veriyi üzerine yazarsa hem grafik hem
  // fiyat çizgileri (priceLine) tutarsız bir duruma düşer; lightweight-charts bu durumda "Value is
  // null" ile çökebiliyordu. fetchTokenRef, her çağrının kendi "nesli"ni damgalar — yanıt geldiğinde
  // artık en güncel çağrı BU DEĞİLSE (ya da grafik bu arada sökülmüşse) sonuç sessizce atlanır.
  const fetchTokenRef = useRef(0);

  const fetchAndDraw = useCallback(async (overrideInterval) => {
    if (!symbol || !token || !charts.current) return;
    const myToken = ++fetchTokenRef.current;
    try {
      const params = new URLSearchParams({ symbol, market });
      if (overrideInterval) params.set('interval', overrideInterval);
      const res = await fetch(`/api/chart/candles?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      if (myToken !== fetchTokenRef.current || !charts.current) return;
      const { candles, formingBar, interval: iv, strategy: strat = 'ma50_rsi',
              ma50, ma200, rsi, rsiMa, hma, adx,
              showHmaIndicator = false, showAdxIndicator = false, longShortRatio: lsr = null } = data;

      if (iv) setCandleInterval(iv);
      setStrategy(strat);
      setShowHma(showHmaIndicator);
      setShowAdx(showAdxIndicator);
      setLongShortRatio(lsr);

      charts.current.formingBar = formingBar || null;
      const { candleSeries, ma50Series, ma200Series, hmaSeries,
              rsiSeries, rsiMaSeries, adxSeries, adx23Line } = charts.current;

      if (candles?.length) { candleSeries.setData(candles); setLastCandle(candles[candles.length - 1]); }

      // MA50/MA200/RSI14/RSI-MA — çekirdek göstergeler, stratejiden bağımsız her zaman çizilir.
      if (ma50?.length)  { ma50Series.setData(ma50);   setMa50Val(ma50[ma50.length - 1]?.value ?? null); }
      else               { ma50Series.setData([]);     setMa50Val(null); }
      if (ma200?.length) { ma200Series.setData(ma200); setMa200Val(ma200[ma200.length - 1]?.value ?? null); }
      else               { ma200Series.setData([]);    setMa200Val(null); }
      if (rsi?.length)   { rsiSeries.setData(rsi);     setRsiVal(rsi[rsi.length - 1]?.value ?? null); }
      else               { rsiSeries.setData([]);      setRsiVal(null); }
      if (rsiMa?.length) { rsiMaSeries.setData(rsiMa); setRsiMaVal(rsiMa[rsiMa.length - 1]?.value ?? null); }
      else               { rsiMaSeries.setData([]);    setRsiMaVal(null); }

      // HMA/ADX — opsiyonel, sadece HMA tabanlı stratejide VE ilgili checkbox açıkken çizilir.
      hmaSeries.setData([]); adxSeries.setData([]); adx23Line.setData([]);
      setHmaVal(null); setAdxVal(null); setHmaDir(null);
      hmaDataRef.current = [];
      if (strat === 'range_hma_adx') {
        // Ham HMA serisi her zaman saklanır (checkbox kapalıyken de) — hover'da yön hesaplamak için.
        if (hma?.length) {
          hmaDataRef.current = hma;
          const lastDir = hma.length >= 2 ? (hma[hma.length - 1].value > hma[hma.length - 2].value ? 1 : (hma[hma.length - 1].value < hma[hma.length - 2].value ? -1 : 0)) : null;
          setHmaDir(lastDir);
        }
        if (showHmaIndicator && hma?.length) {
          hmaSeries.setData(hma);
          setHmaVal(hma[hma.length - 1]?.value ?? null);
        }
        if (showAdxIndicator && adx?.length) {
          adxSeries.setData(adx);
          setAdxVal(adx[adx.length - 1]?.value ?? null);
          const t0 = adx[0].time, t1 = adx[adx.length - 1].time;
          adx23Line.setData([{ time: t0, value: 23 }, { time: t1, value: 23 }]);
        }
      }

      // fitContent SADECE ilk yüklemede değil, periyot değişince de (overrideInterval verildiğinde)
      // çağrılmalı — aksi halde grafik ESKİ periyodun görünür zaman aralığını YENİ (tamamen farklı
      // granülerlikteki) veri kümesine uygulamaya çalışıyor, bu da lightweight-charts'ın iç çizim
      // döngüsünde geçersiz bir bar indeksine düşüp "Value is null" ile çökmesine yol açıyordu.
      // Periyodik aynı-periyot yenilemelerinde (overrideInterval yok) kullanıcının zoom/pan'i KORUNUR.
      if (!initialised.current || overrideInterval) {
        charts.current.chart.timeScale().fitContent();
        initialised.current = true;
      }

      _redrawPriceLines(candleSeries, openTradeRef.current, charts, t('chart.entry'));
    } catch { /* ignore */ }
  }, [symbol, token, t, market]); // eslint-disable-line

  const handleIntervalChange = useCallback((newIv) => {
    setCandleInterval(newIv);
    fetchAndDraw(newIv);
  }, [fetchAndDraw]);

  useEffect(() => { setCandleInterval(''); }, [symbol]);

  useEffect(() => {
    if (!containerRef.current) return;
    const C = getChartColors(themeRef.current);

    const chart = createChart(containerRef.current, {
      width:  containerRef.current.clientWidth,
      height: 454,
      layout: { background: { color: C.bg }, textColor: C.text, fontSize: 10 },
      grid:   { vertLines: { color: C.grid }, horzLines: { color: C.grid } },
      crosshair:       { mode: CrosshairMode.Normal },
      rightPriceScale: { borderColor: C.border },
      timeScale:       { borderColor: C.border, timeVisible: true, secondsVisible: false, fixLeftEdge: true },
    });

    const priceFmt = { type: 'price', precision: 6, minMove: 0.000001 };

    const candleSeries = chart.addSeries(CandlestickSeries, {
      upColor: C.up, downColor: C.down, borderUpColor: C.up, borderDownColor: C.down,
      wickUpColor: C.up, wickDownColor: C.down, priceFormat: priceFmt,
    }, PRICE_PANE);

    const ma50Series = chart.addSeries(LineSeries, {
      color: C.ma50, lineWidth: 1, priceLineVisible: false, lastValueVisible: false, priceFormat: priceFmt,
    }, PRICE_PANE);
    const ma200Series = chart.addSeries(LineSeries, {
      color: C.ma200, lineWidth: 1, priceLineVisible: false, lastValueVisible: false, priceFormat: priceFmt,
    }, PRICE_PANE);
    const hmaSeries = chart.addSeries(LineSeries, {
      color: C.hma, lineWidth: 1, priceLineVisible: false, lastValueVisible: false, priceFormat: priceFmt,
    }, PRICE_PANE);

    const rsiSeries = chart.addSeries(LineSeries, {
      color: C.rsi, lineWidth: 1, priceLineVisible: false, lastValueVisible: false,
    }, IND_PANE);
    const rsiMaSeries = chart.addSeries(LineSeries, {
      color: C.rsiMa, lineWidth: 1, priceLineVisible: false, lastValueVisible: false,
    }, IND_PANE);
    const adxSeries = chart.addSeries(LineSeries, {
      color: C.adx, lineWidth: 1, priceLineVisible: false, lastValueVisible: false,
    }, IND_PANE);
    const adx23Line = chart.addSeries(LineSeries, {
      color: '#94a3b8', lineWidth: 1, lineStyle: LineStyle.Dashed,
      priceLineVisible: false, lastValueVisible: false,
    }, IND_PANE);

    rsiSeries.priceScale().applyOptions({ scaleMargins: { top: 0.1, bottom: 0.1 } });

    // RSI 30/50/70 seviyeleri — RSI için evrensel önemli sınırlar, stratejiden bağımsız her zaman
    // gösterilir. createPriceLine kullanılır çünkü pane genişliği boyunca otomatik uzanır (veri
    // aralığına/time scale'e bağlı değildir, setData ile zaman noktalarını takip etmesi gerekmez).
    rsiSeries.createPriceLine({
      price: 70, color: '#C7C7C7', lineWidth: 1, lineStyle: LineStyle.Solid,
      axisLabelVisible: false, title: '',
    });
    rsiSeries.createPriceLine({
      price: 50, color: '#777777', lineWidth: 1, lineStyle: LineStyle.Dashed,
      axisLabelVisible: false, title: '',
    });
    rsiSeries.createPriceLine({
      price: 30, color: '#C7C7C7', lineWidth: 1, lineStyle: LineStyle.Solid,
      axisLabelVisible: false, title: '',
    });

    const panes = chart.panes();
    if (panes[0]) panes[0].setHeight(320);
    if (panes[1]) panes[1].setHeight(130);

    charts.current = {
      chart, candleSeries,
      ma50Series, ma200Series, hmaSeries,
      rsiSeries, rsiMaSeries,
      adxSeries, adx23Line,
      formingBar: null,
    };

    // Çizim araçları (Trend/Fiyat Aralığı/Yatay-Dikey Çizgi) canvas overlay'i — bkz. useChartDrawings.js.
    const detachDrawings = attachTo(chart, candleSeries, drawCanvasRef.current);
    if (containerRef.current) resizeCanvas(containerRef.current.clientWidth, 454);

    // Fare grafik üzerinde gezerken o anki mumun OHLC + gösterge değerlerini oku (OKX/TradingView'daki
    // gibi) — crosshair grafikten ayrılınca hover null'a döner, legend "son/canlı" değerlere geri düşer.
    chart.subscribeCrosshairMove((param) => {
      if (!param.time || !param.seriesData) { setHover(null); return; }
      const c    = param.seriesData.get(candleSeries);
      const ma50 = param.seriesData.get(ma50Series);
      const ma200 = param.seriesData.get(ma200Series);
      if (!c) { setHover(null); return; }
      const strat = strategyRef.current;
      const rsi   = param.seriesData.get(rsiSeries)?.value   ?? null;
      const rsiMa = param.seriesData.get(rsiMaSeries)?.value ?? null;
      let hma = null, adx = null, hmaDirAtHover = null;
      if (strat === 'range_hma_adx') {
        if (showHmaRef.current) hma = param.seriesData.get(hmaSeries)?.value ?? null;
        if (showAdxRef.current) adx = param.seriesData.get(adxSeries)?.value ?? null;
        // Yön, checkbox kapalı olsa da hesaplanır — ham hma dizisinde hovered zamanı bulup
        // bir önceki noktaya göre kıyaslar (fiyat çizgisi çizilmese de yön bilgisi anlamlıdır).
        const arr = hmaDataRef.current;
        const idx = arr.findIndex(p => p.time === param.time);
        if (idx > 0) {
          hmaDirAtHover = arr[idx].value > arr[idx - 1].value ? 1 : (arr[idx].value < arr[idx - 1].value ? -1 : 0);
        }
      }
      setHover({
        open: c.open, high: c.high, low: c.low, close: c.close,
        ma50: ma50?.value ?? null, ma200: ma200?.value ?? null,
        rsi, rsiMa, hma, adx, hmaDir: hmaDirAtHover,
      });
    });

    const handleResize = () => {
      if (!containerRef.current) return;
      chart.applyOptions({ width: containerRef.current.clientWidth, height: 454 });
      resizeCanvas(containerRef.current.clientWidth, 454);
      recalcHandlePositionsRef.current?.();
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      detachDrawings();
      chart.remove();
      charts.current     = null;
      initialised.current = false;
    };
  }, []); // eslint-disable-line

  // Tema değişince mevcut grafik yeniden oluşturulmaz (veri/zoom kaybolmasın) — sadece
  // arka plan/metin/grid/kenarlık renkleri applyOptions ile güncellenir. Gösterge renkleri sabit.
  useEffect(() => {
    if (!charts.current) return;
    const C = getChartColors(theme);
    charts.current.chart.applyOptions({
      layout: { background: { color: C.bg }, textColor: C.text },
      grid:   { vertLines: { color: C.grid }, horzLines: { color: C.grid } },
      rightPriceScale: { borderColor: C.border },
      timeScale:       { borderColor: C.border },
    });
  }, [theme]);

  useEffect(() => { fetchAndDraw(); }, [fetchAndDraw, analysisTs, settingsSavedAt]);

  useEffect(() => {
    if (!currentPrice || !charts.current?.candleSeries || !charts.current?.formingBar) return;
    const fb = charts.current.formingBar;
    const updated = { time: fb.time, open: fb.open, high: Math.max(fb.high, currentPrice), low: Math.min(fb.low, currentPrice), close: currentPrice };
    charts.current.formingBar = { ...updated };
    charts.current.candleSeries.update(updated);
  }, [currentPrice]);

  useEffect(() => {
    if (!liveAnalysis) return;
    if (liveAnalysis.ma50  != null) setMa50Val(liveAnalysis.ma50);
    if (liveAnalysis.ma200 != null) setMa200Val(liveAnalysis.ma200);
    if (liveAnalysis.rsi   != null) setRsiVal(liveAnalysis.rsi);
    if (liveAnalysis.hma   != null) setHmaVal(liveAnalysis.hma);
    if (liveAnalysis.adx   != null) setAdxVal(liveAnalysis.adx);
    if (liveAnalysis.hmaDirection != null) setHmaDir(liveAnalysis.hmaDirection);
  }, [liveAnalysis]);

  const drawPriceLines = useCallback(() => {
    if (!charts.current) return;
    _redrawPriceLines(charts.current.candleSeries, openTradeRef.current, charts, t('chart.entry'));
  }, []); // eslint-disable-line

  useEffect(() => { drawPriceLines(); }, [openTrade, drawPriceLines]);

  // Tutamaçların ekrandaki (Y piksel) konumunu fiyattan yeniden hesaplar — trade değiştiğinde,
  // pencere yeniden boyutlandığında ve periyodik olarak (zoom/pan sırasında fiyat ekseni kayabilir).
  const recalcHandlePositions = useCallback(() => {
    const C = charts.current;
    const t = openTradeRef.current;
    if (!C?.candleSeries || !t || t.signal_type !== 'manual' || t.status === 'closed') {
      setHandlePos({ tp: null, sl: null });
      return;
    }
    const tp = t.tp_price ? parseFloat(t.tp_price) : null;
    const sl = t.sl_price ? parseFloat(t.sl_price) : null;
    const tpY = tp != null ? C.candleSeries.priceToCoordinate(tp) : null;
    const slY = sl != null ? C.candleSeries.priceToCoordinate(sl) : null;
    setHandlePos({
      tp: tp != null && tpY != null ? { price: tp, y: tpY } : null,
      sl: sl != null && slY != null ? { price: sl, y: slY } : null,
    });
  }, []);
  recalcHandlePositionsRef.current = recalcHandlePositions;

  useEffect(() => { recalcHandlePositions(); }, [openTrade, recalcHandlePositions]);

  // Fiyat pane'i kullanıcı tarafından sürüklenerek yeniden boyutlandırılabilir — lightweight-charts
  // bunun için bir "resize" event'i yayınlamaz, bu yüzden periyodik olarak gerçek yüksekliği okuruz.
  useEffect(() => {
    const id = setInterval(() => {
      const h = charts.current?.chart?.panes?.()[0]?.getHeight?.();
      if (typeof h === 'number' && h > 0) {
        setPricePaneHeight(prev => (Math.abs(prev - h) > 1 ? h : prev));
      }
    }, 300);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!isManualTrade) return;
    // Sürükleme sırasında değil — kullanıcı bırakınca zaten yeniden hesaplanır, sürerken periyodik
    // yeniden hesaplama tutamacı elden kaçırır gibi hissettirir.
    const id = setInterval(() => { if (!dragRef.current.side) recalcHandlePositions(); }, 300);
    return () => clearInterval(id);
  }, [isManualTrade, recalcHandlePositions]);

  const startHandleDrag = (side) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    const cur = handlePos[side];
    if (!cur || !charts.current?.candleSeries) return;
    dragRef.current = { side, startClientY: e.clientY, startY: cur.y };

    const onMove = (ev) => {
      const dy    = ev.clientY - dragRef.current.startClientY;
      const newY  = dragRef.current.startY + dy;
      const price = charts.current?.candleSeries.coordinateToPrice(newY);
      if (price == null) return;
      setGhost(g => ({ ...g, [side]: { y: newY, price } }));
    };
    const onUp = async () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      dragRef.current.side = null;
      const finalGhost = ghostRef.current[side];
      setGhost(g => ({ ...g, [side]: null }));
      if (!finalGhost || !openTradeRef.current?.id) return;
      try {
        await onAdjustTpSl?.(openTradeRef.current.id, {
          [side === 'tp' ? 'tp_price' : 'sl_price']: finalGhost.price,
        });
      } catch (err) {
        // Hata olursa openTrade prop'u bir sonraki fetchTrades ile eski (gerçek) değere döner —
        // recalcHandlePositions bunu otomatik yansıtır, ekstra bir "geri al" mantığı gerekmiyor.
        window.alert(err.message || t('chart.tpSlUpdateFailed'));
      }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const C = getChartColors(theme);

  const cs = candleStats(
    hover?.open  ?? lastCandle?.open,
    hover?.high  ?? lastCandle?.high,
    hover?.low   ?? lastCandle?.low,
    hover?.close ?? lastCandle?.close,
  );

  return (
    <div className="chart-wrapper">
      <div className="chart-legend">
        <span className="legend-item" style={{ color: C.ma50  }}>── MA50{ma50Val  != null ? ` ${fmtPrice(ma50Val)}`  : ''}</span>
        <span className="legend-item" style={{ color: C.ma200 }}>── MA200{ma200Val != null ? ` ${fmtPrice(ma200Val)}` : ''}</span>
        <span className="legend-item" style={{ color: C.rsi   }}>── RSI{rsiVal   != null ? ` ${rsiVal.toFixed(1)}`   : ''}{strategy === 'rsima50' ? t('chart.rsimaRefNote') : ''}</span>
        <span className="legend-item" style={{ color: C.rsiMa }}>── SMA{rsiMaVal != null ? ` ${rsiMaVal.toFixed(1)}` : ''}{strategy === 'rsi_rsima' ? t('chart.rsimaRefNote') : (strategy === 'rsima50' ? t('chart.rsi50Note') : '')}</span>
        {(strategy === 'range_hma_adx') && showHma && (
          <span className="legend-item" style={{ color: C.hma }}>── HMA{hmaVal != null ? ` ${fmtPrice(hmaVal)}` : ''}</span>
        )}
        {(strategy === 'range_hma_adx') && showAdx && (
          <span className="legend-item" style={{ color: C.adx }}>── ADX{adxVal != null ? ` ${adxVal.toFixed(1)}` : ''}</span>
        )}
        {openTrade && <span className="legend-item" style={{ color: '#f59e0b' }}>- - {t('chart.entry')}{openTrade.entry_price ? ` ${fmtPrice(parseFloat(openTrade.entry_price))}` : ''}</span>}
        {openTrade?.tp_price && <span className="legend-item" style={{ color: '#10b981' }}>── TP {fmtPrice(parseFloat(openTrade.tp_price))}</span>}
        {openTrade?.sl_price && <span className="legend-item" style={{ color: '#ef4444' }}>── SL {fmtPrice(parseFloat(openTrade.sl_price))}</span>}
        <span className="legend-symbol">{symbol}</span>
        <select className="chart-interval-select" value={candleInterval} onChange={e => handleIntervalChange(e.target.value)} title={t('chart.periodTitle')}>
          {INTERVALS.map(iv => <option key={iv.value} value={iv.value}>{iv.label}</option>)}
        </select>
        <button className="chart-reset-btn" onClick={handleReset} title={t('chart.resetTitle')}>{t('chart.resetButton')}</button>
      </div>
      <div className="chart-hover-bar">
        <span className="legend-item">O <strong>{fmtPrice(hover?.open  ?? lastCandle?.open)}</strong></span>
        <span className="legend-item">H <strong>{fmtPrice(hover?.high  ?? lastCandle?.high)}</strong></span>
        <span className="legend-item">L <strong>{fmtPrice(hover?.low   ?? lastCandle?.low)}</strong></span>
        <span className="legend-item">C <strong>{fmtPrice(hover?.close ?? lastCandle?.close)}</strong></span>
        <span className="legend-item" style={{ color: C.ma50  }}>MA50 <strong>{fmtPrice(hover?.ma50  ?? ma50Val)}</strong></span>
        <span className="legend-item" style={{ color: C.ma200 }}>MA200 <strong>{fmtPrice(hover?.ma200 ?? ma200Val)}</strong></span>
        <span className="legend-item" style={{ color: C.rsi   }}>RSI <strong>{fmtNum(hover?.rsi   ?? rsiVal)}</strong></span>
        <span className="legend-item" style={{ color: C.rsiMa }}>SMA <strong>{fmtNum(hover?.rsiMa ?? rsiMaVal)}</strong></span>
        {(strategy === 'range_hma_adx') && showHma && (
          <span className="legend-item" style={{ color: C.hma }}>HMA <strong>{fmtPrice(hover?.hma ?? hmaVal)}</strong></span>
        )}
        {(strategy === 'range_hma_adx') && (
          <span className="legend-item" style={{ color: (hover?.hmaDir ?? hmaDir) === 1 ? 'var(--accent)' : (hover?.hmaDir ?? hmaDir) === -1 ? 'var(--red)' : C.hma }}>
            {t('chart.hmaDirection')} <strong>{(hover?.hmaDir ?? hmaDir) === 1 ? t('chart.up') : (hover?.hmaDir ?? hmaDir) === -1 ? t('chart.down') : '—'}</strong>
          </span>
        )}
        {(strategy === 'range_hma_adx') && showAdx && (
          <span className="legend-item" style={{ color: C.adx }}>ADX <strong>{fmtNum(hover?.adx ?? adxVal)}</strong></span>
        )}
        {cs && (
          <>
            <span className="legend-item" style={{ color: cs.green ? 'var(--accent)' : 'var(--red)' }}>{t('chart.akPct')} <strong>{fmtPctSigned(cs.akPct)}</strong></span>
            <span className="legend-item" style={{ color: cs.green ? 'var(--accent)' : 'var(--red)' }}>{t('chart.akVal')} <strong>{fmtPriceSigned(cs.akVal)}</strong></span>
            <span className="legend-item" style={{ color: cs.green ? 'var(--accent)' : 'var(--red)' }}>{t('chart.mmPct')} <strong>{fmtPctSigned(cs.mmPct)}</strong></span>
            <span className="legend-item" style={{ color: cs.green ? 'var(--accent)' : 'var(--red)' }}>{t('chart.mmVal')} <strong>{fmtPriceSigned(cs.mmVal)}</strong></span>
          </>
        )}
      </div>
      <div className="chart-body-row">
        <div className="chart-draw-toolbar" style={{ maxHeight: pricePaneHeight, overflow: 'hidden' }}>
          {DRAW_TOOLS.map(tool => (
            <button
              key={tool.id}
              type="button"
              className={`chart-draw-btn${activeTool === tool.id ? ' active' : ''}`}
              onClick={() => setActiveTool(cur => (cur === tool.id ? null : tool.id))}
              title={t(tool.labelKey)}
            >
              {tool.icon}
            </button>
          ))}
          <div className="chart-draw-sep" />
          <button type="button" className="chart-draw-btn" onClick={clearAll} title={t('chart.drawClearAll')}>
            🗑
          </button>
          {longShortRatio && (
            <>
              <div className="chart-draw-sep" />
              <div
                className="chart-ls-bar"
                title={`${t('chart.longShortRatio')}\n${t('common.short')} ${longShortRatio.shortPct}% · ${t('common.long')} ${longShortRatio.longPct}%`}
              >
                <div className="chart-ls-bar-short" style={{ height: `${longShortRatio.shortPct}%` }} />
                <div className="chart-ls-bar-mid" />
                <div className="chart-ls-bar-long" style={{ height: `${longShortRatio.longPct}%` }} />
              </div>
            </>
          )}
        </div>
        <div ref={containerRef} className="chart-container">
          <canvas ref={drawCanvasRef} className="chart-draw-canvas" />
          {isManualTrade && (ghost.tp || handlePos.tp) && (
            <div
              className={`chart-tpsl-handle chart-tpsl-handle-tp${ghost.tp ? ' dragging' : ''}`}
              style={{ top: (ghost.tp ?? handlePos.tp).y }}
              onMouseDown={startHandleDrag('tp')}
              title={t('chart.dragTpTitle')}
            >
              TP {fmtPrice((ghost.tp ?? handlePos.tp).price)}
            </div>
          )}
          {isManualTrade && (ghost.sl || handlePos.sl) && (
            <div
              className={`chart-tpsl-handle chart-tpsl-handle-sl${ghost.sl ? ' dragging' : ''}`}
              style={{ top: (ghost.sl ?? handlePos.sl).y }}
              onMouseDown={startHandleDrag('sl')}
              title={t('chart.dragSlTitle')}
            >
              SL {fmtPrice((ghost.sl ?? handlePos.sl).price)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const DRAW_TOOLS = [
  { id: 'trend', icon: '╱', labelKey: 'chart.drawTrend' },
  { id: 'range', icon: '▭', labelKey: 'chart.drawRange' },
  { id: 'hline', icon: '─', labelKey: 'chart.drawHLine' },
  { id: 'vline', icon: '│', labelKey: 'chart.drawVLine' },
];

function _redrawPriceLines(candleSeries, trade, charts, entryLabel) {
  if (!candleSeries) return;
  (charts.current?.priceLines || []).forEach(pl => { try { candleSeries.removePriceLine(pl); } catch (_) {} });
  if (charts.current) charts.current.priceLines = [];
  if (!trade) return;
  const mkLine = (price, color, label, style = LineStyle.Solid) =>
    candleSeries.createPriceLine({ price, color, lineWidth: 1, lineStyle: style, axisLabelVisible: true, title: `${label} ${fmtPrice(price)}` });
  const lines = [];
  if (trade.entry_price) lines.push(mkLine(parseFloat(trade.entry_price), '#f59e0b', entryLabel, LineStyle.Dashed));
  if (trade.tp_price)    lines.push(mkLine(parseFloat(trade.tp_price),    '#10b981', 'TP'));
  if (trade.sl_price)    lines.push(mkLine(parseFloat(trade.sl_price),    '#ef4444', 'SL'));
  if (charts.current) charts.current.priceLines = lines;
}
